<script setup>
/*
========================================================================
StepProduct_Modal_ProductSelection.vue

Modal for selecting product size/variant/lumen output in the Product step.
Uses ProductSizeSelector which updates the store DIRECTLY on selection.
This allows users to close the modal at any time and their selection is
already saved.

This is different from the Report step modal which uses local state until
the user explicitly confirms (to allow cancellation without side effects).

Uses BaseModalPanel for consistent modal behavior and smooth transitions.
========================================================================
*/
import { useMainStore } from "@/stores/store.js";
import { Utils } from "@/utils.js";
import { PhTable, PhArrowSquareOut } from "@phosphor-icons/vue";
import BaseModalPanel from "./utils/BaseModalPanel.vue";
import ProductSizeSelector from "./utils/ProductSizeSelector.vue";
import SizeGuide from "./utils/SizeGuide.vue";

const store = useMainStore();

/*
------------------------------------------------------------------------
Props
------------------------------------------------------------------------
*/
const props = defineProps({
	// Controls modal visibility
	show: {
		type: Boolean,
		required: true,
	},
	// Function to close the modal (provided by parent StepProduct.vue)
	fnCancelModal: {
		type: Function,
		required: true,
	},
});
</script>

<template>
	<!--
    ========================================================================
    Product Selection Modal
    Wrapped in BaseModalPanel for consistent modal behavior (backdrop,
    escape handling, focus trap, smooth transitions).
    ========================================================================
    -->
	<BaseModalPanel
		:show="show"
		:on-close="fnCancelModal"
		:show-backdrop="true"
		:show-close-button="true"
	>
		<!--
        ====================================================================
        Modal Content - Two column layout
        ====================================================================
        -->
		<div class="flex h-full flex-col sm:flex-row">
			<!--
            ----------------------------------------------------------------
            Left Section: Product Image (sticky on desktop)
            ----------------------------------------------------------------
            -->
			<div
				class="flex-0 min-h-[150px] sm:sticky sm:right-0 sm:top-0 sm:h-screen sm:w-[35%] md:w-[35%]"
			>
				<img
					class="absolute left-3 top-0 mx-auto max-w-[150px] sm:max-w-[180px] md:max-w-[300px]"
					:src="Utils.resolveHeroImage(store.overlays.product.data?.images?.hero, store.currentProduct?.shape)"
					:alt="`Product image of: ${store.overlays.product.data?.name}.`"
				/>
			</div>

			<!--
            ----------------------------------------------------------------
            Right Section: Product Details and Selection Form
            Uses ProductSizeSelector which updates store directly on selection.
            ----------------------------------------------------------------
            -->
			<div
				class="lux-modal-content relative flex h-screen flex-1 flex-col overflow-y-auto px-8 sm:w-[65%] md:w-[65%]"
			>
				<!-- Product Name -->
				<h2 class="flex-0 lux-page-heading pb-0 pr-8 pt-8 font-normal">
					{{ store.overlays.product.data?.name }}
				</h2>

				<!--
                ----------------------------------------------------------------
                Size/Variant/Lumen Selector (updates store directly)
                ----------------------------------------------------------------
                -->
				<div class="flex-0 mb-4 bg-white">
					<div class="mt-0 grid grid-cols-1 sm:grid-cols-6">
						<div class="sm:col-span-6 sm:col-start-1 flex flex-col gap-3">
							<ProductSizeSelector />
						</div>
					</div>
				</div>

				<!--
                ----------------------------------------------------------------
                Product Description and Spec Sheet Link
                ----------------------------------------------------------------
                -->
				<div class="flex-0 mb-5 sm:pr-5">
					<p
						class="lux-copy-normal first-of-type:mt-0"
						v-html="store.overlays.product.data?.desc"
					></p>
					<p class="mt-5 flex items-center gap-1">
						<PhTable size="16" weight="regular" aria-hidden="true" />
						<a
							:href="store.overlays.product.data?.spec_sheet_permalink"
							target="_blank"
							class="text-sm underline outline-none hover:underline focus:ring-2 focus:ring-accent focus:ring-offset-1"
							title="Opens in a new tab/window."
							rel="noopener"
						>
							View the spec sheet.
						</a>
						<PhArrowSquareOut
							size="11"
							weight="regular"
							aria-hidden="true"
							class="relative -mt-2"
						/>
						<span class="sr-only">(link opens in a new window)</span>
					</p>
				</div>

				<!--
                ----------------------------------------------------------------
                Size Guide Images (collapsible)
                ----------------------------------------------------------------
                -->
				<SizeGuide
					:images="store.overlays.product.data?.images?.dimensions || []"
					:productName="store.overlays.product.data?.name || ''"
					wrapperClass="pb-20"
				/>
			</div>
		</div>
	</BaseModalPanel>
</template>

<style scoped>
/* Styles inherited from BaseModalPanel */
</style>
